import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle, Plug, ScanSearch } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '../../components/Button';
import { StatusBadge } from '../../components/StatusBadge';
import { useAuth } from '../auth/AuthProvider';
import { toProblem } from './problem';
import {
  channelsKey,
  detectChannels,
  rediscoverChannels,
  type Rediscovery,
  devicesKey,
  listChannels,
  probeChannels,
  probeErrorLabels,
  setChannelsEnabled,
  statusView,
  type ChannelDetection,
  type ChannelProbe,
  type Device,
} from './api';

interface Props {
  recorder: Device;
  canManage: boolean;
  /** Opens a channel's own drawer (name, enabled, notes). */
  onOpenChannel: (channel: Device) => void;
  /** Applies a detected channel count to the recorder form (saved with it). */
  onUseCount: (count: number) => void;
}

const probeShort: Partial<Record<string, string>> = {
  stream_not_found: 'Sem vídeo neste canal',
  unauthorized: 'Senha recusada',
  unreachable: 'Inacessível',
  timeout: 'Sem resposta',
  blocked: 'Bloqueado pela política de rede',
  not_rtsp: 'Não é RTSP',
};

/**
 * A DVR/NVR's channels (ADR-018): test every channel, detect the channel
 * count over ONVIF, and enable or disable channels in bulk.
 */
export function ChannelsPanel({ recorder, canManage, onOpenChannel, onUseCount }: Props) {
  const { request } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [results, setResults] = useState<Map<number, ChannelProbe> | null>(null);
  const [detection, setDetection] = useState<ChannelDetection | null>(null);
  const [rediscovery, setRediscovery] = useState<Rediscovery | null>(null);
  const onvifRecorder = recorder.connection === 'onvif';

  const channels = useQuery({
    queryKey: channelsKey(recorder.id),
    queryFn: () => listChannels(request, recorder.id),
    refetchInterval: 15_000,
  });
  const list = channels.data ?? [];

  const afterChange = async (updated: Device[]) => {
    qc.setQueryData(channelsKey(recorder.id), updated);
    await qc.invalidateQueries({ queryKey: devicesKey, exact: false });
  };

  const probe = useMutation({
    mutationFn: () => probeChannels(request, recorder.id),
    onSuccess: async (res) => {
      setResults(new Map(res.map((r) => [r.device.channel ?? 0, r])));
      await afterChange(res.map((r) => r.device));
    },
  });
  const detect = useMutation({
    mutationFn: () => detectChannels(request, recorder.id),
    onSuccess: setDetection,
  });
  const rediscover = useMutation({
    mutationFn: () => rediscoverChannels(request, recorder.id),
    onSuccess: async (res) => {
      setRediscovery(res);
      await qc.invalidateQueries({ queryKey: channelsKey(recorder.id) });
      await qc.invalidateQueries({ queryKey: devicesKey, exact: false });
    },
  });
  const toggle = useMutation({
    mutationFn: (enable: boolean) => {
      const nums = [...selected];
      return setChannelsEnabled(
        request,
        recorder.id,
        enable ? { enable: nums } : { disable: nums },
      );
    },
    onSuccess: async (updated) => {
      setSelected(new Set());
      await afterChange(updated);
    },
  });

  const numbers = list.map((c) => c.channel ?? 0);
  const allSelected = numbers.length > 0 && numbers.every((n) => selected.has(n));
  const pick = (n: number, on: boolean) => {
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(n);
      else next.delete(n);
      return next;
    });
  };
  // Channels the recorder answered for but without video, still enabled.
  // Other failures (unreachable, password) are the recorder's, not the channel's.
  const noVideo = results
    ? list.filter(
        (c) => c.enabled && results.get(c.channel ?? 0)?.error_code === 'stream_not_found',
      )
    : [];
  const failures = results ? [...results.values()].filter((r) => !r.ok) : [];
  const recorderFailure =
    results &&
    failures.length === results.size &&
    failures.every(
      (r) => r.error_code !== 'stream_not_found' && r.error_code === failures[0]?.error_code,
    )
      ? (failures[0]?.error_code ?? 'protocol_error')
      : null;
  const busy = probe.isPending || toggle.isPending;

  return (
    <section aria-labelledby="channels-heading" className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3
            id="channels-heading"
            className="text-2xs font-medium tracking-wide text-fg-subtle uppercase"
          >
            Canais
          </h3>
          <p className="mt-1 text-xs text-fg-muted">
            {recorder.channel_count} canais · {list.filter((c) => c.enabled).length} ativos. Cada
            canal é uma câmera no ao vivo, na gravação e nos mosaicos.
          </p>
        </div>
        <StatusBadge tone={statusView[recorder.status].tone}>
          {statusView[recorder.status].label}
        </StatusBadge>
      </div>

      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              probe.mutate();
            }}
            disabled={busy || list.length === 0}
          >
            {probe.isPending ? (
              <LoaderCircle aria-hidden="true" className="size-4 animate-spin" strokeWidth={2} />
            ) : (
              <Plug aria-hidden="true" className="size-4" strokeWidth={1.75} />
            )}
            {probe.isPending ? 'Testando canais…' : 'Testar canais'}
          </Button>
          {onvifRecorder ? (
            <Button
              variant="ghost"
              onClick={() => {
                rediscover.mutate();
              }}
              disabled={rediscover.isPending}
            >
              {rediscover.isPending ? (
                <LoaderCircle aria-hidden="true" className="size-4 animate-spin" strokeWidth={2} />
              ) : (
                <ScanSearch aria-hidden="true" className="size-4" strokeWidth={1.75} />
              )}
              {rediscover.isPending ? 'Perguntando ao gravador…' : 'Redescobrir canais (ONVIF)'}
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={() => {
                detect.mutate();
              }}
              disabled={detect.isPending}
            >
              {detect.isPending ? (
                <LoaderCircle aria-hidden="true" className="size-4 animate-spin" strokeWidth={2} />
              ) : (
                <ScanSearch aria-hidden="true" className="size-4" strokeWidth={1.75} />
              )}
              Detectar quantidade (ONVIF)
            </Button>
          )}
        </div>
      )}

      <div aria-live="polite" className="space-y-2">
        {(probe.isError || detect.isError || toggle.isError) && (
          <Notice tone="crit">Não foi possível concluir a operação. Tente novamente.</Notice>
        )}
        {rediscover.isError && (
          <Notice tone="crit">
            {toProblem(rediscover.error).fields.connection ??
              'Não foi possível concluir a operação. Tente novamente.'}
          </Notice>
        )}
        {rediscovery && !rediscover.isPending && (
          <Notice tone="neutral">{rediscoverySummary(rediscovery)}</Notice>
        )}
        {detection &&
          (detection.ok && detection.channels !== null ? (
            <Notice tone="neutral">
              O gravador informa {detection.channels}{' '}
              {detection.channels === 1 ? 'entrada' : 'entradas'} de vídeo.
              {detection.channels !== recorder.channel_count && detection.channels > 0 && (
                <Button
                  variant="ghost"
                  className="ml-1 h-6 px-2 text-accent!"
                  onClick={() => {
                    onUseCount(detection.channels ?? 0);
                  }}
                >
                  Usar {detection.channels} canais
                </Button>
              )}
            </Notice>
          ) : (
            <Notice tone="crit">
              Não foi possível detectar pela ONVIF
              {detection.error_code ? `: ${probeErrorLabels[detection.error_code]}` : '.'} Escolha a
              quantidade manualmente.
            </Notice>
          ))}
        {recorderFailure && (
          <Notice tone="crit">Nenhum canal respondeu: {probeErrorLabels[recorderFailure]}</Notice>
        )}
        {noVideo.length > 0 && canManage && (
          <Notice tone="warn">
            {noVideo.length === 1 ? '1 canal ativo' : `${String(noVideo.length)} canais ativos`} sem
            vídeo no teste.
            <Button
              variant="ghost"
              className="ml-1 h-6 px-2 text-accent!"
              onClick={() => {
                setSelected(new Set(noVideo.map((c) => c.channel ?? 0)));
              }}
            >
              Selecionar para desativar
            </Button>
          </Notice>
        )}
      </div>

      {canManage && selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-line-strong bg-surface-raised px-3 py-1.5 text-[13px]">
          <span className="mr-auto text-fg-muted">
            {selected.size} {selected.size === 1 ? 'selecionado' : 'selecionados'}
          </span>
          <Button
            variant="ghost"
            className="h-7"
            disabled={busy}
            onClick={() => {
              toggle.mutate(true);
            }}
          >
            Ativar
          </Button>
          <Button
            variant="ghost"
            className="h-7 text-crit!"
            disabled={busy}
            onClick={() => {
              toggle.mutate(false);
            }}
          >
            Desativar
          </Button>
        </div>
      )}

      {channels.isError && !channels.data ? (
        <Notice tone="crit">Não foi possível carregar os canais.</Notice>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-left text-xs" aria-busy={channels.isFetching}>
            <caption className="sr-only">Canais de {recorder.name}</caption>
            <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
              <tr className="border-b border-line">
                {canManage && (
                  <th scope="col" className="w-px px-3 py-1.5">
                    <input
                      type="checkbox"
                      aria-label="Selecionar todos os canais"
                      checked={allSelected}
                      onChange={(e) => {
                        setSelected(e.target.checked ? new Set(numbers) : new Set());
                      }}
                      className="size-3.5 accent-[var(--color-accent-strong)]"
                    />
                  </th>
                )}
                <th scope="col" className="px-3 py-1.5 font-medium">
                  Canal
                </th>
                <th scope="col" className="px-3 py-1.5 font-medium">
                  Status
                </th>
                <th scope="col" className="px-3 py-1.5 font-medium">
                  Vídeo
                </th>
              </tr>
            </thead>
            <tbody>
              {!channels.data
                ? Array.from({ length: 4 }, (_, i) => (
                    <tr key={i} aria-hidden="true" className="border-b border-line last:border-0">
                      <td colSpan={canManage ? 4 : 3} className="px-3 py-2.5">
                        <div className="h-3 w-40 animate-pulse rounded bg-surface-hover" />
                      </td>
                    </tr>
                  ))
                : list.map((c) => {
                    const n = c.channel ?? 0;
                    const r = results?.get(n);
                    const st = statusView[c.status];
                    return (
                      <tr
                        key={c.id}
                        className={`border-b border-line last:border-0 ${c.enabled ? '' : 'opacity-60'}`}
                      >
                        {canManage && (
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              aria-label={`Selecionar ${c.name}`}
                              checked={selected.has(n)}
                              onChange={(e) => {
                                pick(n, e.target.checked);
                              }}
                              className="size-3.5 accent-[var(--color-accent-strong)]"
                            />
                          </td>
                        )}
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => {
                              onOpenChannel(c);
                            }}
                            className="text-left text-fg decoration-fg-subtle underline-offset-2 hover:underline"
                          >
                            <span className="mr-2 font-mono text-fg-subtle">
                              {String(n).padStart(2, '0')}
                            </span>
                            {c.name}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          {c.enabled ? (
                            <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                          ) : (
                            <StatusBadge tone="neutral">Desativado</StatusBadge>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <ChannelVideo probe={r} />
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ChannelVideo({ probe }: { probe: ChannelProbe | undefined }) {
  if (!probe) return <span className="text-fg-subtle">—</span>;
  if (!probe.ok) {
    const code = probe.error_code ?? 'protocol_error';
    return (
      <span className="text-crit" title={probeErrorLabels[code]}>
        {probeShort[code] ?? 'Falhou'}
      </span>
    );
  }
  const s = probe.stream;
  const detail = s
    ? [s.encoding, s.width && s.height ? `${String(s.width)}×${String(s.height)}` : '']
        .filter(Boolean)
        .join(' · ')
    : '';
  // Many DVRs answer an SDP for a channel with no camera: no resolution then.
  return <span className="text-fg-muted">{detail || 'Responde (sem resolução)'}</span>;
}

function Notice({ tone, children }: { tone: 'crit' | 'warn' | 'neutral'; children: ReactNode }) {
  const cls = {
    crit: 'border-crit/30 bg-crit-soft text-crit',
    warn: 'border-warn/30 bg-warn-soft text-fg',
    neutral: 'border-line-strong bg-canvas text-fg',
  }[tone];
  return (
    <div
      role={tone === 'crit' ? 'alert' : 'status'}
      className={`flex flex-wrap items-center gap-x-1 gap-y-1 rounded-md border px-3 py-2 text-[13px] ${cls}`}
    >
      {tone !== 'neutral' && (
        <CircleAlert aria-hidden="true" className="mr-1 size-4 shrink-0" strokeWidth={2} />
      )}
      {children}
    </div>
  );
}

const nums = (l: number[]) => l.join(', ');

/** What a rediscovery changed, in one sentence. */
function rediscoverySummary(r: Rediscovery): string {
  const parts: string[] = [];
  if (r.added.length) parts.push(`canais adicionados: ${nums(r.added)}`);
  if (r.updated.length) parts.push(`endereços atualizados: ${nums(r.updated)}`);
  if (r.missing.length)
    parts.push(
      `não listados pelo gravador (mantidos; exclua ou desative se sobrarem): ${nums(r.missing)}`,
    );
  if (!parts.length) return 'O gravador informou os mesmos canais: nada mudou.';
  const text = parts.join('; ');
  return text.charAt(0).toUpperCase() + text.slice(1) + '.';
}
